import { useMemo } from 'react';
import { useIntl } from 'umi';
import { toActiveUsersTag, toActiveUsersTagToCN } from '@intelligence/utils/common';
import { SortMode } from '@intelligence/pages/TopCharts/_config/enums';
import { MultiDataSourceTopChartsPageType } from '@intelligence/v3/pages/TopCharts/MobileGames/_config/pageConfigs';
import { LANGUAGE_CN } from '@home/_config/config';
import {
  ACTIVE_METRICS,
  AU_TITLE_MAP,
  DATA_SOURCE_NAME_MAP,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';

/**
 * @description 榜单可视化卡片指标、排序指标相关的文案
 * @param props 配置
 * @param props.topChartsType 榜单落地页类型
 * @param props.fetchParams 榜单落地页数据查询参数对象
 */
export default function useTextInfo(props: { topChartsType: string; fetchParams: IRecord }) {
  const { topChartsType, fetchParams } = props;

  const dateType = fetchParams?.date_type || 'daily';
  const orderSource = fetchParams?.order_source || '';
  const orderMetric = fetchParams?.order_metric || '';
  const ratioType = fetchParams?.ratio_type || '';

  const { locale, formatMessage } = useIntl();

  const titleNow = useMemo<string>(() => {
    let orderMetricName = '';

    switch (topChartsType) {
      case MultiDataSourceTopChartsPageType.ActiveUsers:
      case MultiDataSourceTopChartsPageType.DataBrainCalibration: {
        const orderMetricNameGranularityMap = locale === LANGUAGE_CN ? toActiveUsersTagToCN : toActiveUsersTag;
        orderMetricName = orderMetricNameGranularityMap?.[dateType] || '';
        break;
      }
      case MultiDataSourceTopChartsPageType.Revenue: {
        orderMetricName = formatMessage({ id: 'dbi_revenue' });
        break;
      }
      case MultiDataSourceTopChartsPageType.Downloads: {
        orderMetricName = formatMessage({ id: 'dbi_downloads' });
        break;
      }
      default: {
        const orderMetricObj = ACTIVE_METRICS.find((item) => item?.value === orderMetric);
        orderMetricName = orderMetricObj?.label || '';
      }
    }

    return orderMetricName;
  }, [locale, formatMessage, topChartsType, dateType, orderMetric]);

  // 根据排序获得Sorted by 标题
  const orderNameInfo = useMemo<string>(() => {
    if (!fetchParams) return '';
    const orderSourceName = DATA_SOURCE_NAME_MAP[orderSource] || '';
    const orderMetricObj = ACTIVE_METRICS.find((item) => item?.value === orderMetric);
    const orderMetricName = orderMetricObj?.label || AU_TITLE_MAP[dateType] || '';
    const result = `${
      topChartsType === MultiDataSourceTopChartsPageType.ActiveUsers ||
      topChartsType === MultiDataSourceTopChartsPageType.DataBrainCalibration
        ? orderSourceName
        : ''
    } ${orderMetricName}`;
    return ratioType === SortMode.Circle || topChartsType === MultiDataSourceTopChartsPageType.Revenue
      ? `${result}(${orderSource === 'sensortower' ? '$' : '%'})`
      : result;
  }, [topChartsType, fetchParams, dateType, orderSource, orderMetric, ratioType]);

  return { titleNow, orderNameInfo };
}
